import React from 'react';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import { Trans } from 'react-i18next';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';

export interface BookingClientOptionRowProps {
  name: string;
  /** First and last name kept by the school, when it differs from the account name. */
  organizationName?: string | null;
  phoneNumber?: string | null;
  profileImageUrl?: string | null;
  bandName?: string | null;
  blockedLabel?: string | null;
  /** Tighter row for the closed select value. */
  compact?: boolean;
}

const BookingClientOptionRow: React.FC<BookingClientOptionRowProps> = ({
  name,
  organizationName,
  phoneNumber,
  profileImageUrl,
  bandName,
  blockedLabel,
  compact = false,
}) => {
  const avatarSize = compact ? 32 : 40;
  const trimmedBand = bandName?.trim() || null;
  const trimmedPhone = phoneNumber?.trim() || null;
  const trimmedOrganization = organizationName?.trim() || null;
  const showPhone = Boolean(trimmedPhone && trimmedPhone !== name.trim());

  return (
    <Box sx={{ display: 'flex', alignItems: compact ? 'center' : 'flex-start', gap: 1.25, minWidth: 0, width: '100%' }}>
      <Avatar
        src={profileImageUrl || undefined}
        sx={{
          width: avatarSize,
          height: avatarSize,
          fontSize: compact ? '0.75rem' : '0.85rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {tutorAvatarInitials(name)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {name}
        </Typography>
        {trimmedOrganization ? (
          <Typography variant="body2" color="text.secondary" noWrap sx={{ fontWeight: 500, lineHeight: 1.3 }}>
            {trimmedOrganization}
          </Typography>
        ) : null}
        {trimmedBand ? (
          <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap sx={{ mt: 0.15 }}>
            <MusicNoteOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" component="span" noWrap>
              <Trans
                i18nKey="common.userBandAffiliation"
                defaults="Plays in <bandName>{{name}}</bandName>"
                values={{ name: trimmedBand }}
                components={{
                  bandName: (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    />
                  ),
                }}
              />
            </Typography>
          </Stack>
        ) : null}
        {!compact && showPhone ? (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {trimmedPhone}
          </Typography>
        ) : null}
        {!compact && blockedLabel ? (
          <Typography variant="caption" color="error.main" noWrap display="block">
            {blockedLabel}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default BookingClientOptionRow;
